import React, { useState, useEffect, useContext, useRef } from "react";
import { TextField, Box, InputAdornment } from "@mui/material";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import WizardDataContext from '../../../store/WizardDataContext';
import { SOCIAL_PLATFORM_BASES } from '../../../utils/urlUtils';

export default function CompanyDetails({ setDisableBtn }) {

  const urlRegex = /^(https?|ftp):\/\/(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;

  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const company = wizardData.company;

  const [emailNotValid, setEmailNotValid] = useState(false)
  const [websiteNotValid, setWebsiteNotValid] = useState(false)
  const [companyNameNotValid, setCompanyNameNotValid] = useState(false)
  const [mangerNameNotValid, setMangerNameNotValid] = useState(false)
  const [phoneNumberNotValid, setPhoneNumberNotValid] = useState(false)
  const [companyDescriptionNotValid, setCompanyDescriptionNotValid] = useState(false)
  const [phoneValue, setPhoneValue] = useState('')
  const [userCountry, setUserCountry] = useState('us')
  const [isDetectingCountry, setIsDetectingCountry] = useState(true)
  const [isPhoneFocused, setIsPhoneFocused] = useState(false)
  const phoneInputRef = useRef(null)

  const detectUserCountry = async () => {
    try {
      setIsDetectingCountry(true);
      const response = await fetch('https://api.country.is/');
      const data = await response.json();
      
      if (data.country) {
        // Convert country code to lowercase for PhoneInput compatibility
        const countryCode = data.country.toLowerCase();
        
        // Only change country if we don't have an existing phone number
        // This prevents PhoneInput from clearing the input when country changes
        if (!company.managerNumber || company.managerNumber.trim() === '') {
          setUserCountry(countryCode);
        }
        
        // Store the detected country in the context for use in other steps
        updateData('meta', { userCountry: countryCode });
        
        // DO NOT clear existing phone numbers - preserve them for editing
      }
    } catch (error) {
      // Only set default country if no existing phone number
      if (!company.managerNumber || company.managerNumber.trim() === '') {
        setUserCountry('us');
      }
      updateData('meta', { userCountry: 'us' });
      // DO NOT clear existing phone numbers in error case either
    } finally {
      setIsDetectingCountry(false);
    }
  };

  useEffect(() => {
    // Initialize phone value from context if it exists (for editing scenarios)
    if (company.managerNumber && company.managerNumber.trim() !== '') {
      setPhoneValue(company.managerNumber);
    }
    
    detectUserCountry();
    
    const fallbackTimer = setTimeout(() => {
      if (isDetectingCountry) {
        setIsDetectingCountry(false);
      }
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  // Add focus/blur event listeners to phone input
  useEffect(() => {
    const phoneInputElement = phoneInputRef.current?.querySelector('input');
    if (phoneInputElement) {
      const handleFocus = () => setIsPhoneFocused(true);
      const handleBlur = () => setIsPhoneFocused(false);
      
      phoneInputElement.addEventListener('focus', handleFocus);
      phoneInputElement.addEventListener('blur', handleBlur);
      
      return () => {
        phoneInputElement.removeEventListener('focus', handleFocus);
        phoneInputElement.removeEventListener('blur', handleBlur);
      };
    }
  }, [phoneValue, userCountry]);

  // Watch for context changes and update phone value when context loads
  useEffect(() => {
    if (company.managerNumber && company.managerNumber.trim() !== '' && !phoneValue) {
      setPhoneValue(company.managerNumber);
    }
  }, [company.managerNumber, phoneValue]);

  useEffect(() => {
    if (company.name.trim() !== '') {
      setCompanyNameNotValid(false);
    }
    if (company.email.trim() !== '' && /(([^\s@&&s@]+\.[^\s@]+))$/.test(company.email)) {
      setEmailNotValid(false);
    }
    if (company.managerName.trim() !== '') {
      setMangerNameNotValid(false);
    }

    if (company.managerNumber.trim() !== '' && company.managerNumber.match(/^(\+\d{1,3}[- ]?)?\d{15}$/)) {
      setPhoneNumberNotValid(false);
    }
    if (company.website.trim() !== '' && company.website !== 'https://') {
      if (urlRegex.test(company.website)) {
        setWebsiteNotValid(false);
      } else {
        setWebsiteNotValid(true);
      }
    }
    // Don't set websiteNotValid when empty/https:// on mount (same as company name - error only after user clears)
    if (company.description && company.description.trim() !== '') {
      setCompanyDescriptionNotValid(false);
    }

    // Set default https:// for website if empty
    if (!company.website || company.website.trim() === '') {
      updateData('company', { website: 'https://' });
    }

  }, [company, updateData]);

  // Prefill social fields with platform base URLs (like website's https://) so user can add their profile path
  useEffect(() => {
    const updates = {};
    if (!company.facebookPage || company.facebookPage.trim() === '') updates.facebookPage = SOCIAL_PLATFORM_BASES.facebook;
    if (!company.instagramProfile || company.instagramProfile.trim() === '') updates.instagramProfile = SOCIAL_PLATFORM_BASES.instagram;
    if (!company.pinterestProfile || company.pinterestProfile.trim() === '') updates.pinterestProfile = SOCIAL_PLATFORM_BASES.pinterest;
    if (Object.keys(updates).length > 0) {
      updateData('company', updates);
    }
  }, []);


  useEffect(() => {

    if (
      company.name.trim() !== '' &&
      company.email.trim() !== '' &&
      company.managerName.trim() !== '' &&
      company.managerNumber.trim() !== '' &&
      company.website.trim() !== '' && company.website !== 'https://' &&
      company.description && company.description.trim() !== '' &&
      !emailNotValid &&
      !websiteNotValid &&
      !companyNameNotValid &&
      !mangerNameNotValid &&
      !phoneNumberNotValid &&
      !companyDescriptionNotValid
    ) {
      setDisableBtn(false)
    } else {
      setDisableBtn(true)
    }

  }, [company, emailNotValid, websiteNotValid, companyNameNotValid, mangerNameNotValid, phoneNumberNotValid, companyDescriptionNotValid, setDisableBtn])


  const changeCompanyName = (e) => {
    let value = e.target.value
    updateData('company', { name: value })
    if (value.trim() !== '') {
      setCompanyNameNotValid(false)
    } else {
      setCompanyNameNotValid(true)
    }
  }

  const changeEmail = (e) => {
    let value = e.target.value
    updateData('company', { email: value })
    if (/(([^\s@&&s@]+\.[^\s@]+))$/.test(value) && value.trim() !== '') {
      setEmailNotValid(false)
    } else {
      setEmailNotValid(true)
      return true
    }
  }

  const changeManagerName = (e) => {
    let value = e.target.value
    updateData('company', { managerName: value })
    if (value.trim() !== '') {
      setMangerNameNotValid(false)
    } else {
      setMangerNameNotValid(true)
      return true
    }
  }



  const handlePhoneChange = (value, country) => {
    // Extract the country code from the current value
    const currentCountryCode = country.dialCode;
    
    // If the value starts with the country code, allow it
    // If not, prepend the country code
    let finalValue = value;
    if (!value.startsWith(currentCountryCode)) {
      finalValue = currentCountryCode + value;
    }
    
    setPhoneValue(finalValue);
    updateData('company', { managerNumber: finalValue });

    // Validate phone number - check if it has sufficient digits after country code
    // Most country codes are 1-4 digits, so we check for at least 7 total digits
    if (finalValue && finalValue.length >= 7) {
      setPhoneNumberNotValid(false);
    } else {
      setPhoneNumberNotValid(true);
    }
  }

  const handleCountryChange = (country) => {
    setUserCountry(country.iso2);
    
    // If there's an existing phone number, update it with the new country code
    if (phoneValue && phoneValue.length > 0) {
      // Extract the phone number without country code (remove leading digits)
      const phoneWithoutCountry = phoneValue.replace(/^\d+/, '');
      const newPhoneValue = country.dialCode + phoneWithoutCountry;
      setPhoneValue(newPhoneValue);
      updateData('company', { managerNumber: newPhoneValue });
      
      // Re-validate the phone number with new country code
      if (newPhoneValue.length >= 7) {
        setPhoneNumberNotValid(false);
      } else {
        setPhoneNumberNotValid(true);
      }
    } else {
      // If no phone number exists, just clear the validation
      setPhoneNumberNotValid(false);
    }
  }

  const changeCompanyWebsite = (e) => {
    let value = e.target.value
    updateData('company', { website: value })
    if (value.trim() === '' || value === 'https://') {
      setWebsiteNotValid(true)
    } else if (urlRegex.test(value)) {
      setWebsiteNotValid(false)
    } else {
      setWebsiteNotValid(true)
    }
  }

  const changeCompanyDescription = (e) => {
    let value = e.target.value
    updateData('company', { description: value })
    if (value.trim() !== '') {
      setCompanyDescriptionNotValid(false)
    } else {
      setCompanyDescriptionNotValid(true)
    }
  }

  // Strip the platform base URL prefix from value so the editable input only
  // contains the user's handle/path. The full URL (base + handle) is what we
  // store in context, so downstream payloads keep the existing behavior.
  const stripBase = (value, platform) => {
    const base = SOCIAL_PLATFORM_BASES[platform] || '';
    if (!value) return '';
    return value.startsWith(base) ? value.slice(base.length) : value;
  };

  const handleSocialChange = (platform, key) => (e) => {
    const handle = e.target.value;
    const base = SOCIAL_PLATFORM_BASES[platform] || '';
    updateData('company', { [key]: base + handle });
  };

  const changeFacebookPage = handleSocialChange('facebook', 'facebookPage');
  const changeInstagramProfile = handleSocialChange('instagram', 'instagramProfile');
  const changePinterestProfile = handleSocialChange('pinterest', 'pinterestProfile');

  const socialInputProps = {
    disableUnderline: true,
    sx: {
      '& input': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
        transition: 'border-bottom-color 200ms',
      },
      '& input:hover': { borderBottom: '1px solid rgba(0, 0, 0, 0.87)' },
      '& input:focus': { borderBottom: '2px solid #1976d2' },
    },
  };


  const changeBackgroundColor = (e) => {
    let value = e.target.value
    updateData('company', { backgroundColor: value })
  }

  const handleColorChange = (color) => {
    updateData('company', { backgroundColor: color })
  }

  return (
    <>
      <div className="container">
        <div className="step1-container">
          <div className="step1">
            <div className="step1-heading">
              <h1>Add Company Details</h1>
            </div>
            <div className="step1-input">

              <div className="company-details-grid">
                <div className="company-details-grid-item">
                  <TextField error={companyNameNotValid} helperText={companyNameNotValid ? "Please enter a company name" : null} onChange={changeCompanyName} value={company.name} required label="COMPANY NAME*" variant="standard" fullWidth />
                </div>
                <div className="company-details-grid-item">
                  <TextField error={mangerNameNotValid} helperText={mangerNameNotValid ? "Please enter a contact person" : null} onChange={changeManagerName} value={company.managerName} required label="CONTACT PERSON*" variant="standard" fullWidth />
                </div>
                <div className="company-details-grid-item">
                  <TextField error={emailNotValid} helperText={emailNotValid ? "Please enter a valid email" : null} onChange={changeEmail} value={company.email} required label="EMAIL ADDRESS*" variant="standard" fullWidth />
                </div>
                <div className="company-details-grid-item company-details-grid-item-phone">
                  <Box sx={{ position: 'relative', width: '100%' }} className={isPhoneFocused && !phoneNumberNotValid ? 'phone-input-focused' : ''}>
                    <span className={`phone-label ${isPhoneFocused && !phoneNumberNotValid ? 'phone-label-focused' : ''}`} style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '0',
                      fontSize: '12px',
                      color: phoneNumberNotValid
                        ? '#d32f2f'
                        : (isPhoneFocused ? '#1976d2' : '#666'),
                      zIndex: 2,
                      backgroundColor: 'white',
                      paddingRight: '4px',
                      transition: 'color 0s ease'
                    }}>
                      PHONE NUMBER*
                      {isDetectingCountry && (
                        <span style={{ marginLeft: '8px', fontSize: '10px', color: '#999' }}>
                          (Detecting location...)
                        </span>
                      )}
                    </span>
                    <div ref={phoneInputRef}>
                      <PhoneInput
                        country={userCountry}
                        value={phoneValue}
                        onChange={handlePhoneChange}
                        onCountryChange={handleCountryChange}
                        containerClass={isPhoneFocused && !phoneNumberNotValid ? 'phone-input-container-focused' : ''}
                        inputStyle={{
                          width: '100%',
                          height: '40px',
                          border: 'none',
                          borderBottom: phoneNumberNotValid
                            ? '2px solid #d32f2f'
                            : (isPhoneFocused
                              ? '2px solid #1976d2'
                              : '1px solid rgba(0, 0, 0, 0.42)'),
                          borderRadius: '0',
                          fontSize: '16px',
                          padding: '8px 12px 0px 50px',
                          backgroundColor: 'transparent',
                          outline: 'none',
                          fontFamily: 'inherit',
                          transition: 'border-bottom 0s ease'
                        }}
                        buttonStyle={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          borderBottom: phoneNumberNotValid
                            ? '2px solid #d32f2f'
                            : (isPhoneFocused
                              ? '2px solid #1976d2'
                              : '1px solid rgba(0, 0, 0, 0.42)'),
                          borderRadius: '0',
                          padding: '8px 4px',
                          transition: 'border-bottom 0s ease'
                        }}
                        dropdownStyle={{ minWidth: '250px', textAlign: 'left' }}
                        enableSearch={true}
                        searchPlaceholder="Search country..."
                        searchNotFound="No country found"
                        preferredCountries={['us', 'gb', 'in', 'ca', 'au']}
                        disableCountryCode={false}
                        countryCodeEditable={false}
                      />
                    </div>
                    {phoneNumberNotValid && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-20px',
                        left: '0',
                        color: '#d32f2f',
                        fontSize: '12px',
                        margin: 0
                      }}>
                        Please enter a valid phone number.
                      </div>
                    )}
                  </Box>
                </div>
                <div className="company-details-grid-item">
                  <TextField
                  error={websiteNotValid}
                  helperText={websiteNotValid ? (company.website.trim() === '' || company.website === 'https://' ? "Please enter a company website" : "Please enter a valid website address") : null}
                  onChange={changeCompanyWebsite}
                  value={company.website}
                  required
                  label="COMPANY WEBSITE*"
                  variant="standard"
                  placeholder="https://example.com"
                  fullWidth
                />
                </div>
                <div className="company-details-grid-item">
                  <TextField
                    onChange={changeFacebookPage}
                    value={stripBase(company.facebookPage, 'facebook')}
                    label="FACEBOOK PAGE"
                    variant="standard"
                    placeholder="yourpage"
                    fullWidth
                    InputProps={{
                      ...socialInputProps,
                      startAdornment: (
                        <InputAdornment position="start" disableTypography sx={{ color: 'rgba(0,0,0,0.87)', mr: 0 }}>
                          {SOCIAL_PLATFORM_BASES.facebook}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="company-details-grid-item">
                  <TextField
                    onChange={changeInstagramProfile}
                    value={stripBase(company.instagramProfile, 'instagram')}
                    label="INSTAGRAM PROFILE"
                    variant="standard"
                    placeholder="yourprofile"
                    fullWidth
                    InputProps={{
                      ...socialInputProps,
                      startAdornment: (
                        <InputAdornment position="start" disableTypography sx={{ color: 'rgba(0,0,0,0.87)', mr: 0 }}>
                          {SOCIAL_PLATFORM_BASES.instagram}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="company-details-grid-item">
                  <TextField
                    onChange={changePinterestProfile}
                    value={stripBase(company.pinterestProfile, 'pinterest')}
                    label="PINTEREST PROFILE"
                    variant="standard"
                    placeholder="yourprofile"
                    fullWidth
                    InputProps={{
                      ...socialInputProps,
                      startAdornment: (
                        <InputAdornment position="start" disableTypography sx={{ color: 'rgba(0,0,0,0.87)', mr: 0 }}>
                          {SOCIAL_PLATFORM_BASES.pinterest}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>

              <div className="input-full-width company-description-field-wrap">
                <TextField
                  className="company-description-field"
                  error={companyDescriptionNotValid}
                  helperText={companyDescriptionNotValid ? "Please add a company description" : null}
                  onChange={changeCompanyDescription}
                  value={company.description}
                  style={{ marginTop: "20px", width: "100%" }}
                  required
                  label="COMPANY DESCRIPTION*"
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </div>
            </div>

          </div>
        </div>
      </div>

    </>

  )
}
